import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CheckPermissionsDirective } from '@parashift/shared/directives';
import { TenantConfiguration } from '@parashift/shared/models';
import { GenAiModelsRegister } from '@parashift/shared/services';
import { InputFieldComponent, SelectFieldComponent } from '@parashift/shared/components';
import { SelectOption } from '@parashift/shared/utils';

@Component({
  selector: 'general-form',
  templateUrl: './general-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CheckPermissionsDirective,
    ReactiveFormsModule,
    InputFieldComponent,
    SelectFieldComponent
  ]
})
export class GeneralFormComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private genAiModelsRegister = inject(GenAiModelsRegister);
  private destroyRef = inject<DestroyRef>(DestroyRef);

  @Input({ required: true }) model: TenantConfiguration['plainModel'];
  @Input({ required: true }) tenantId: number;

  @Input({ required: true })
  set disabled(disabled) {
    this._disabled = disabled;
    disabled ? this.form?.disable() : this.form?.enable();
  }
  get disabled(): boolean { return this._disabled; }
  private _disabled: boolean;

  @Output() saveForm = new EventEmitter<TenantConfiguration['plainModel']>();

  form: FormGroup;
  genAiModelOptions = signal<SelectOption[]>([]);

  ngOnInit() {
    this.initForm();

    this.genAiModelsRegister.getModels(this.tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.rebuildOptions();
        this.cdr.markForCheck();
      });

    this.form.get('gen_ai_model_id').valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.rebuildOptions();
        this.cdr.markForCheck();
      });
  }

  submit() {
    this.form?.markAllAsTouched();
    this.form?.updateValueAndValidity({ emitEvent: true });
    this.cdr.detectChanges();
    this.form?.valid && this.emitForm();
  }

  private rebuildOptions() {
    const currentId = this.model.gen_ai_model_id ? String(this.model.gen_ai_model_id) : null;
    const selectedId = this.form.get('gen_ai_model_id').value as string | null;
    this.genAiModelOptions.set(this.genAiModelsRegister.buildModelOptions(currentId, selectedId));
  }

  private initForm() {
    this.form = this.formBuilder.group({
      quota: [this.model.quota ?? 1000, [Validators.required, Validators.min(1)]],
      gen_ai_model_id: [this.model.gen_ai_model_id ? String(this.model.gen_ai_model_id) : null],
      separation_model_id: [this.model.separation_model_id ?? null],
    });
  }

  private emitForm() {
    const value = this.form.getRawValue();
    value.gen_ai_model_id = value.gen_ai_model_id ? Number(value.gen_ai_model_id) : null;
    this.saveForm.emit(Object.assign(this.model, value));
  }
}
